import { useEffect } from "react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SEOHead } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  createWebPageSchema,
} from "@/hooks/useStructuredData";

const Kontakt = () => {
  const { language } = useLanguage();

  useEffect(() => {
    const win = window as any;
    const p = function (a: any, ar: any) { a.q.push(ar); };
    const d = document;

    win.Cal = win.Cal || function () {
      const cal = win.Cal;
      const ar = arguments;
      if (!cal.loaded) {
        cal.ns = {};
        cal.q = cal.q || [];
        const s = d.head.appendChild(d.createElement("script"));
        s.src = "https://app.cal.com/embed/embed.js";
        cal.loaded = true;
      }
      if (ar[0] === "init") {
        const api = function () { p(api, arguments); };
        const namespace = ar[1];
        api.q = api.q || [];
        if (typeof namespace === "string") {
          cal.ns[namespace] = cal.ns[namespace] || api;
          p(cal.ns[namespace], ar);
          p(cal, ["initNamespace", namespace]);
        } else p(cal, ar);
        return;
      }
      p(cal, ar);
    };

    win.Cal("init", "get-a-demo", { origin: "https://app.cal.com" });

    win.Cal.ns["get-a-demo"]("inline", {
      elementOrSelector: "#my-cal-inline-get-a-demo",
      config: { layout: "month_view", theme: "light" },
      calLink: "chathero/get-a-demo"
    });

    win.Cal.ns["get-a-demo"]("ui", {
      theme: "light",
      cssVarsPerTheme: {
        light: { "cal-brand": "#323ABB" },
        dark: { "cal-brand": "#FAFAFA" }
      },
      hideEventTypeDetails: false,
      layout: "month_view"
    });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="kontakt" locale={language} />
      <StructuredDataHead schemas={[
        organizationSchema,
        createWebPageSchema(
          "Kontakt | Chathero",
          "Kontaktieren Sie Chathero – Buchen Sie Ihre persönliche Demo.",
          "/kontakt",
          [
            { name: "Home", url: "/" },
            { name: "Kontakt", url: "/kontakt" },
          ]
        ),
      ]} />
      <LLMMetadataHead
        title="Kontakt – Persönliche Chathero Demo | Chathero"
        description="Buchen Sie Ihre persönliche Chathero Demo. In 15 Minuten zeigen wir, wie Sie Kommunikation, Service und Vertrieb mit KI automatisieren."
        keywords="Kontakt, Demo, Chathero, Beratung, AI Demo"
        canonicalUrl="https://chathero.ai/kontakt"
        aiCategory="Kontakt, Demo, Beratung"
        aiEntityType="Contact Page"
        aiUseCase="Demo anfragen, Beratungsgespräch buchen, Kontaktaufnahme"
      />
      <Header />
      <main className="container mx-auto px-4 pt-40 pb-16 max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {language === "de"
              ? "Ihre persönliche Chathero Demo"
              : "Your personal Chathero demo"}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            {language === "de"
              ? "In 15 Minuten zeigen wir, wie Sie Kommunikation, Service und Vertrieb mit KI automatisieren – konkret, praxisnah und auf Ihre Ziele abgestimmt."
              : "In 15 minutes, we'll show how to automate communication, service, and sales with AI—concrete, practical, and aligned to your goals."}
          </p>

          <p className="text-lg font-semibold text-foreground mb-8">
            {language === "de"
              ? "Jetzt buchen und sehen, wie KI Ihren Geschäftserfolg steigert."
              : "Book now and see how AI boosts your business success."}
          </p>
        </div>

        {/* Cal.com Inline Embed */}
        <div className="w-full rounded-2xl overflow-hidden border border-border bg-card">
          <div id="my-cal-inline-get-a-demo" style={{ width: "100%", minHeight: "700px" }} />
        </div>

        {/* Contact Info */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-4">
            {language === "de" ? "Oder kontaktieren Sie uns direkt" : "Or contact us directly"}
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-muted-foreground">
            <div>
              <p className="font-semibold text-foreground">Chathero GmbH</p>
              <p>Elsässerstr. 24, 81667 München</p>
            </div>
            <div>
              <p><a href="tel:+498944499973" className="hover:text-primary transition-colors">+49 89 444 999 73</a></p>
              <p><a href="mailto:info@chathero.ai" className="hover:text-primary transition-colors">info@chathero.ai</a></p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Kontakt;
